import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, UserCheck, Clock, BookOpen, PlaySquare, CheckCircle2,
  BookPlus, Boxes, UserPlus, GraduationCap, Upload, CreditCard, ArrowRight, Presentation, ClipboardList, ClipboardCheck, Scissors } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { OnlineNow, RecentAccess, AccessAlerts, FailedLogins } from "@/components/AccessWidgets";
import { ServerMini } from "@/components/ServerMini";

const Stat = ({ icon: Icon, label, value, color, testid, to }) => {
  const corpo = (
    <>
      <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-500">
        <span className="h-7 w-7 rounded-lg grid place-items-center shrink-0" style={{ background: `${color}18`, color }}><Icon className="h-4 w-4" /></span>
        <span className="leading-tight">{label}</span>
      </div>
      <div className="mt-2.5 font-display text-[26px] leading-none font-bold text-slate-900">{Number(value ?? 0).toLocaleString("pt-BR")}</div>
    </>
  );
  const cls = "bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all block";
  return to ? <Link to={to} data-testid={testid} className={cls}>{corpo}</Link> : <div data-testid={testid} className={cls}>{corpo}</div>;
};

const SHORTCUTS = [
  { label: "Novo curso", icon: BookPlus, to: "/admin/cursos", color: "#232D4F" },
  { label: "Nova turma", icon: Boxes, to: "/admin/turmas", color: "#1E9E2A" },
  { label: "Cadastrar aluno", icon: UserPlus, to: "/admin/alunos", color: "#232D4F" },
  { label: "Novo professor", icon: GraduationCap, to: "/admin/professores", color: "#1E9E2A" },
  { label: "Importar alunos", icon: Upload, to: "/admin/alunos", color: "#232D4F" },
  { label: "Matrículas", icon: ClipboardList, to: "/admin/matriculas", color: "#1E9E2A" },
  { label: "Provas", icon: ClipboardCheck, to: "/admin/provas", color: "#232D4F" },
  { label: "Financeiro", icon: CreditCard, to: "/admin/financeiro/vendas", color: "#E65100" },
];
// atalho leva para a gestão do IEP Salão (de lá, "Abrir o IEP Salão" entra no sistema)
const SALAO = { label: "IEP Salão", icon: Scissors, to: "/admin/salao", color: "#926B38" };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Welcome banner */}
      <div className="aluna-hero relative rounded-3xl overflow-hidden">
        <img src="/brand/login-arte-frascos.webp" alt="" className="aluna-hero-frascos" />
        <img src="/brand/aluna-hero-arte.webp" alt="" className="aluna-hero-mulher" />
        <div className="relative px-6 py-7 sm:py-9 sm:pl-[118px] sm:pr-10 max-w-2xl">
          <p className="text-[#1E9E2A] text-sm font-bold">Bem-vindo(a) de volta 👋</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A2340] mt-1">{user?.name}</h1>
          <p className="text-slate-600 mt-2">Painel de gestão do IEP – In'Nova Educação Profissional.</p>
        </div>
      </div>

      <ServerMini />

      {/* Atalhos */}
      <div>
        <h2 className="font-display text-lg font-bold text-slate-800 mb-3">Atalhos</h2>
        <div className={`grid grid-cols-4 gap-2 sm:gap-3 ${user?.salao ? "sm:grid-cols-5 lg:grid-cols-9" : "lg:grid-cols-8"}`}>
          {(user?.salao ? [...SHORTCUTS, SALAO] : SHORTCUTS).map((s) => (
            <button
              key={s.label}
              data-testid={`shortcut-${s.to.split("/").pop()}-${s.label.split(" ")[0].toLowerCase()}`}
              onClick={() => (s.href ? window.open(s.href, "_blank", "noopener") : navigate(s.to))}
              className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all px-1.5 py-3 flex flex-col items-center text-center gap-1.5 group min-w-0"
            >
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl grid place-items-center text-white transition-transform group-hover:scale-105" style={{ background: s.color }}>
                <s.icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <span className="text-[11px] sm:text-xs font-semibold text-slate-600 leading-tight">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Métricas */}
      {!data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
          <Stat icon={Users} label="Total de alunos" value={data.total_students} color="#232D4F" testid="stat-total-students" to="/admin/alunos" />
          <Stat icon={UserCheck} label="Alunos cadastrados" value={data.registered_students} color="#1E9E2A" testid="stat-registered-students" to="/admin/alunos" />
          <Stat icon={Clock} label="Convites pendentes" value={data.pending_students} color="#E65100" testid="stat-pending-students" />
          <Stat icon={BookOpen} label="Cursos online" value={data.online_courses ?? data.total_courses} color="#232D4F" testid="stat-total-courses" to="/admin/cursos" />
          <Stat icon={Presentation} label="Cursos presenciais" value={data.presencial_courses} color="#B45309" testid="stat-presencial-courses" to="/admin/cursos" />
          <Stat icon={PlaySquare} label="Aulas" value={data.total_lessons} color="#1E9E2A" testid="stat-total-lessons" />
          <Stat icon={CheckCircle2} label="Aulas concluídas" value={data.total_completions} color="#16A34A" testid="stat-completions" />
        </div>
      )}

      {/* Recentes */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-slate-800">Alunos recentes</h2>
            <button onClick={() => navigate("/admin/alunos")} className="text-xs font-semibold text-[#232D4F] flex items-center gap-1 hover:gap-1.5 transition-all">
              Ver todos <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {!data ? (
            <div className="p-4 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : data.recent_students.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">Nenhum aluno ainda.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_students.map((s) => (
                <li key={s.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "#1E9E2A" }}>
                    {(s.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 text-sm truncate">{s.name}</div>
                    <div className="text-xs text-slate-400 truncate">{s.email}</div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.status === "cadastrado" ? "bg-teal-50 text-teal-700" : s.status === "importado" ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"}`}>
                    {s.status === "cadastrado" ? "Cadastrado" : s.status === "importado" ? "Importado" : "Pendente"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-slate-800">Turmas</h2>
            <button onClick={() => navigate("/admin/turmas")} className="text-xs font-semibold text-[#232D4F] flex items-center gap-1 hover:gap-1.5 transition-all">
              Ver todas <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {groups.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">Nenhuma turma criada ainda.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {groups.map((g) => (
                <li key={g.id} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/turmas/${g.id}`)}>
                  <div className="h-8 w-8 rounded-lg grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
                    <Boxes className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 text-sm truncate">{g.name}</div>
                    <div className="text-xs text-slate-400 truncate">{g.instructor_name || "Sem professor"}</div>
                  </div>
                  <div className="text-xs text-slate-500 shrink-0">{g.student_count} alunos</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* acompanhamento de acessos */}
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <OnlineNow />
        <AccessAlerts />
        <RecentAccess />
        <FailedLogins />
      </div>
    </div>
  );
}
